<template>
  <div class="site-settings-container">
    <!-- Header Section -->
    <div class="settings-header">
      <div class="header-content">
        <div class="header-icon">
          <v-icon size="32" color="white">mdi-cog-outline</v-icon>
        </div>
        <div class="header-text">
          <h1 class="settings-title">{{ $t("site_settings") }}</h1>
          <p class="settings-subtitle">Configure your site icons and AI support</p>
        </div>
      </div>
    </div>

    <!-- Warning Alert -->
    <v-alert
      class="danger-alert"
      type="warning"
      prominent
      icon="mdi-alert-circle"
      border="left"
      colored-border
      elevation="2"
    >
      <div class="alert-content">
        <h3 class="alert-title">⚠️ Danger Zone</h3>
        <p class="alert-text">
          Please don't change the icons if you are not familiar with it. 
          Contact the developer if you have any questions.
        </p>
      </div>
    </v-alert>

    <!-- Settings Sections -->
    <div class="settings-sections">
      <!-- Causes Section -->
      <v-card class="settings-section" elevation="4">
        <v-card-title class="section-header">
          <div class="section-icon">
            <v-icon color="primary" size="24">mdi-heart-outline</v-icon>
          </div>
          <div class="section-title">
            <h3>Causes</h3>
            <p>Manage charitable causes and their icons</p>
          </div>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="toggleSection('causes')"
            class="section-toggle"
          >
            <v-icon>
              {{ sectionsExpanded.causes ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-expand-transition>
          <div v-show="sectionsExpanded.causes" class="section-content">
            <v-divider class="section-divider"></v-divider>
            
            <div class="icons-grid-container">
              <div class="icons-grid">
                <!-- Existing Icons -->
                <div
                  v-for="item in causes"
                  :key="item._id"
                  class="icon-card"
                  @click="selectIcon(item, 'Cause')"
                >
                  <div class="icon-preview">
                    <site-icon :name="item.name" type="Cause" />
                  </div>
                  <div class="icon-info">
                    <span class="icon-name">{{ item.name }}</span>
                    <div class="icon-actions">
                      <v-btn icon small color="primary" @click.stop="selectIcon(item, 'Cause')">
                        <v-icon size="16">mdi-pencil</v-icon>
                      </v-btn>
                      <!-- <v-btn icon small color="error" @click.stop="deleteIcon(item, 'Cause')">
                        <v-icon size="16">mdi-delete</v-icon>
                      </v-btn> -->
                    </div>
                  </div>
                </div>

                <!-- Add New Icon -->
                <div class="icon-card add-icon-card" @click="addIcon('Cause')">
                  <div class="add-icon-content">
                    <v-icon size="48" color="primary">mdi-plus-circle-outline</v-icon>
                    <span class="add-icon-text">Add New Cause</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-expand-transition>
      </v-card>

      <!-- Interests Section -->
      <v-card class="settings-section" elevation="4">
        <v-card-title class="section-header">
          <div class="section-icon">
            <v-icon color="secondary" size="24">mdi-star-outline</v-icon>
          </div>
          <div class="section-title">
            <h3>Interests</h3>
            <p>Manage user interests and their icons</p>
          </div>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="toggleSection('interests')"
            class="section-toggle"
          >
            <v-icon>
              {{ sectionsExpanded.interests ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-expand-transition>
          <div v-show="sectionsExpanded.interests" class="section-content">
            <v-divider class="section-divider"></v-divider>
            
            <div class="icons-grid-container">
              <div class="icons-grid">
                <!-- Existing Icons -->
                <div
                  v-for="item in interests"
                  :key="item._id"
                  class="icon-card"
                  @click="selectIcon(item, 'Interest')"
                >
                  <div class="icon-preview">
                    <site-icon :name="item.name" type="Interest" />
                  </div>
                  <div class="icon-info">
                    <span class="icon-name">{{ item.name }}</span>
                    <div class="icon-actions">
                      <v-btn icon small color="primary" @click.stop="selectIcon(item, 'Interest')">
                        <v-icon size="16">mdi-pencil</v-icon>
                      </v-btn>
                      <!-- <v-btn icon small color="error" @click.stop="deleteIcon(item, 'Interest')">
                        <v-icon size="16">mdi-delete</v-icon>
                      </v-btn> -->
                    </div>
                  </div>
                </div>

                <!-- Add New Icon -->
                <div class="icon-card add-icon-card" @click="addIcon('Interest')">
                  <div class="add-icon-content">
                    <v-icon size="48" color="secondary">mdi-plus-circle-outline</v-icon>
                    <span class="add-icon-text">Add New Interest</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-expand-transition>
      </v-card>

      <!-- AI Support Section -->
      <v-card class="settings-section" elevation="4">
        <v-card-title class="section-header">
          <div class="section-icon">
            <v-icon color="success" size="24">mdi-robot-outline</v-icon>
          </div>
          <div class="section-title">
            <h3>AI Support Initial Data</h3>
            <p>Configure initial data for AI chatbot support</p>
          </div>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="toggleSection('aiSupport')"
            class="section-toggle"
          >
            <v-icon>
              {{ sectionsExpanded.aiSupport ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-expand-transition>
          <div v-show="sectionsExpanded.aiSupport" class="section-content">
            <v-divider class="section-divider"></v-divider>
            
            <div class="ai-support-form">
              <div class="form-header">
                <v-icon color="success" size="20">mdi-information-outline</v-icon>
                <span class="form-help-text">
                  Keep the initial data clear and concise for better AI performance
                </span>
              </div>

              <v-textarea
                v-model="chatDataText"
                label="Initial AI Support Data"
                placeholder="Enter clear and concise initial data for AI support..."
                outlined
                auto-grow
                rows="4"
                counter
                :rules="[rules.required]"
                class="ai-textarea"
                prepend-inner-icon="mdi-message-text-outline"
              />

              <div class="form-actions">
                <v-btn
                  color="success"
                  @click="onSaveChatData"
                  :loading="loading"
                  :disabled="!chatDataText || chatDataText.length === 0"
                  class="save-btn"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  Save Configuration
                </v-btn>

                <v-btn
                  text
                  @click="resetChatData"
                  :disabled="loading"
                  class="reset-btn"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Reset
                </v-btn>
              </div>

              <!-- Save Success Feedback -->
              <v-fade-transition>
                <v-alert
                  v-if="showSaveSuccess"
                  type="success"
                  dense
                  text
                  class="save-success-alert"
                >
                  <v-icon slot="prepend" small>mdi-check-circle</v-icon>
                  AI Support data saved successfully!
                </v-alert>
              </v-fade-transition>
            </div>
          </div>
        </v-expand-transition>
      </v-card>
    </div>

    <!-- Loading Overlay -->
    <v-overlay :value="loading" class="loading-overlay">
      <div class="loading-content">
        <v-progress-circular
          indeterminate
          size="64"
          color="primary"
          width="4"
        />
        <p class="loading-text">Loading settings...</p>
      </div>
    </v-overlay>

    <!-- Dialogs -->
    <site-icon-add
      :onClose="onCloseDialog"
      :type="dialogType"
      :dialog="addDialog"
      v-if="addDialog"
    />
    <site-icon-edit
      :onClose="onCloseDialog"
      :type="dialogType"
      :icon="selected"
      :dialog="editDialog"
      v-if="editDialog && selected"
    />

    <!-- Delete Confirmation Dialog -->
    <!-- <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="delete-dialog-title">
          <v-icon color="error" size="24">mdi-alert-circle</v-icon>
          <span class="ml-2">Confirm Delete</span>
        </v-card-title>
        <v-card-text class="delete-dialog-text">
          Are you sure you want to delete "<strong>{{ selectedForDelete?.name }}</strong>"?
          This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="confirmDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog> -->
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import SiteIconAdd from "./SiteIconAdd.vue";
import SiteIconEdit from "./SiteIconEdit.vue";
import SiteIcon from "@/components/common/SiteIcon.vue";

export default {
  name: "EnhancedSiteSettings",
  components: { SiteIconAdd, SiteIcon, SiteIconEdit },
  
  data() {
    return {
      loading: false,
      addDialog: false,
      editDialog: false,
      deleteDialog: false,
      dialogType: "",
      selected: null,
      selectedForDelete: null,
      deleteType: "",
      chatDataText: "",
      showSaveSuccess: false,
      sectionsExpanded: {
        causes: true,
        interests: false,
        aiSupport: false
      },
      rules: {
        required: value => !!value || "This field is required."
      }
    };
  },

  methods: {
    ...mapActions({
      getCauseIcons: "icon/getCauseIcons",
      getInterestIcons: "icon/getInterestIcons",
      setChatbotData: "usersetting/setChatbotData",
      getChatbotData: "usersetting/getChatbotData"
    }),

    toggleSection(section) {
      this.sectionsExpanded[section] = !this.sectionsExpanded[section];
    },

    addIcon(type) {
      this.dialogType = type;
      this.addDialog = true;
    },

    onCloseDialog() {
      this.addDialog = false;
      this.editDialog = false;
      // Refresh data after dialog closes
      this.loadIcons();
    },

    selectIcon(item, type) {
      this.dialogType = type;
      this.selected = item;
      this.editDialog = true;
    },

    deleteIcon(item, type) {
      this.selectedForDelete = item;
      this.deleteType = type;
      this.deleteDialog = true;
    },

    confirmDelete() {
      // Implement delete logic here
      console.log(`Deleting ${this.deleteType}:`, this.selectedForDelete);
      this.deleteDialog = false;
      this.selectedForDelete = null;
      this.deleteType = "";
      // Refresh data after delete
      this.loadIcons();
    },

    onSaveChatData() {
      if (this.chatDataText.length) {
        this.loading = true;
        this.setChatbotData({ value: this.chatDataText })
          .then(res => {
            console.log(res);
            this.loading = false;
            this.showSaveSuccess = true;
            
            // Hide success message after 3 seconds
            setTimeout(() => {
              this.showSaveSuccess = false;
            }, 3000);
          })
          .catch(err => {
            this.loading = false;
            console.error(err.response?.data?.message || err.message);
            // Show error message here
          });
      }
    },

    resetChatData() {
      this.chatDataText = "";
    },

    loadIcons() {
      this.loading = true;
      
      Promise.all([
        this.getCauseIcons(),
        this.getInterestIcons()
      ])
      .then(() => {
        this.loading = false;
      })
      .catch(error => {
        this.loading = false;
        console.error("Error loading icons:", error);
      });
    },

    loadChatbotData() {
      this.getChatbotData()
        .then(res => {
          this.chatDataText = res.value || "";
        })
        .catch(error => {
          console.error("Error loading chatbot data:", error.response?.data?.message || error.message);
          this.chatDataText = "";
        });
    }
  },

  computed: {
    ...mapState("icon", {
      interests: "interests",
      causes: "causes"
    })
  },

  mounted() {
    this.loadIcons();
    this.loadChatbotData();
  }
};
</script>

<style scoped>
.site-settings-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  /* background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%); */
  min-height: 100vh;
}

/* Header Styles */
.settings-header {
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 32px;
  color: white;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 20px;
}

.header-icon {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-text {
  flex: 1;
}

.settings-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 8px;
  color: white;
}

.settings-subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
  margin: 0;
}

/* Warning Alert */
.danger-alert {
  margin-bottom: 32px;
  border-radius: 12px !important;
  background: linear-gradient(135deg, #fff3e0 0%, #ffecb3 100%) !important;
}

.alert-content {
  padding: 8px 0;
}

.alert-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #f57c00;
  margin-bottom: 8px;
}

.alert-text {
  color: #e65100;
  margin: 0;
  line-height: 1.5;
}

/* Settings Sections */
.settings-sections {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.settings-section {
  border-radius: 16px !important;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.settings-section:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
  transform: translateY(-2px);
}

.section-header {
  background: linear-gradient(135deg, #f8f9ff 0%, #f1f3ff 100%);
  padding: 24px !important;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.section-header:hover {
  background: linear-gradient(135deg, #f1f3ff 0%, #e8ecff 100%);
}

.section-icon {
  background: white;
  border-radius: 10px;
  padding: 8px;
  margin-right: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.section-title h3 {
  font-size: 1.3rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.section-title p {
  color: #64748b;
  font-size: 0.9rem;
  margin: 0;
}

.section-toggle {
  background: rgba(102, 126, 234, 0.1) !important;
  border-radius: 8px !important;
}

.section-content {
  padding: 0 24px 24px;
}

.section-divider {
  margin: 0 0 24px 0 !important;
}

/* Icons Grid */
.icons-grid-container {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
}

.icons-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
}

.icon-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  position: relative;
  overflow: hidden;
}

.icon-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border-color: #667eea;
}

.icon-preview {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.icon-info {
  text-align: center;
}

.icon-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: #2c3e50;
  display: block;
  margin-bottom: 8px;
}

.icon-actions {
  display: flex;
  justify-content: center;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.icon-card:hover .icon-actions {
  opacity: 1;
}

/* Add Icon Card */
.add-icon-card {
  border: 2px dashed #ccc;
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
}

.add-icon-card:hover {
  border-color: #667eea;
  background: #f8f9ff;
}

.add-icon-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.add-icon-text {
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 500;
}

/* AI Support Form */
.ai-support-form {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 24px;
}

.form-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.form-help-text {
  color: #64748b;
  font-size: 0.9rem;
}

.ai-textarea {
  margin-bottom: 16px;
}

.form-actions {
  display: flex;
  gap: 16px;
  align-items: center;
}

.save-btn {
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%) !important;
  color: white !important;
  border-radius: 8px !important;
  padding: 0 24px !important;
  height: 48px !important;
  font-weight: 500;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4);
}

.reset-btn {
  color: #64748b !important;
  text-transform: none;
}

.save-success-alert {
  margin-top: 16px;
  border-radius: 8px;
}



.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.loading-text {
  font-size: 1.1rem;
  color: #64748b;
  font-weight: 500;
}

/* Delete Dialog */
.delete-dialog-title {
  color: #d32f2f;
  font-weight: 600;
}

.delete-dialog-text {
  font-size: 1rem;
  line-height: 1.5;
}

/* Responsive Design */
@media (max-width: 768px) {
  .site-settings-container {
    padding: 16px;
  }

  .settings-header {
    padding: 24px 20px;
  }

  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }

  .settings-title {
    font-size: 2rem;
  }

  .section-header {
    padding: 20px 16px !important;
  }

  .section-content {
    padding: 0 16px 20px;
  }

  .icons-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 12px;
  }

  .icon-card {
    padding: 16px;
  }

  .ai-support-form {
    padding: 20px;
  }

  .form-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .save-btn {
    width: 100% !important;
  }
}

@media (max-width: 480px) {
  .settings-header {
    padding: 20px 16px;
  }

  .settings-title {
    font-size: 1.8rem;
  }

  .icons-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }

  .section-header {
    padding: 16px 12px !important;
  }

  .section-content {
    padding: 0 12px 16px;
  }

  .icons-grid-container {
    padding: 16px;
  }

  .ai-support-form {
    padding: 16px;
  }
}

/* Dark theme support */
/* @media (prefers-color-scheme: dark) {
  .site-settings-container {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  }

  .settings-section {
    background: #2c3e50 !important;
    border-color: rgba(255, 255, 255, 0.1);
  }

  .section-header {
    background: linear-gradient(135deg, #34495e 0%, #2c3e50 100%);
  }

  .section-title h3 {
    color: #ecf0f1;
  }

  .section-title p {
    color: #bdc3c7;
  }

  .icon-card {
    background: #34495e;
    border-color: rgba(255, 255, 255, 0.1);
  }

  .icon-name {
    color: #ecf0f1;
  }

  .icons-grid-container {
    background: #2c3e50;
  }

  .ai-support-form {
    background: #2c3e50;
  }

  .add-icon-card {
    background: #34495e;
    border-color: rgba(255, 255, 255, 0.3);
  }

  .add-icon-text {
    color: #bdc3c7;
  }
} */
</style>

